/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param, jsdoc/require-returns */
import { ReactElement, useRef } from 'react';
import { useElementWidth } from '../ParticleBins/particleBinShared';

export type ParameterStats = {
  count: number;
  min: number;
  max: number;
  mean: number;
  p05: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
  latest: number;
};

/** Linear-interpolated quantile of an ascending-sorted array. */
function quantile(sorted: number[], q: number): number {
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

/** Window statistics for one parameter; null when there are no readings. */
export function computeStats(values: number[], latest: number | null): ParameterStats | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const sum = sorted.reduce((acc, v) => acc + v, 0);
  return {
    count: sorted.length,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    mean: sum / sorted.length,
    p05: quantile(sorted, 0.05),
    p25: quantile(sorted, 0.25),
    p50: quantile(sorted, 0.5),
    p75: quantile(sorted, 0.75),
    p95: quantile(sorted, 0.95),
    latest: latest ?? sorted[sorted.length - 1]
  };
}

export function fmt(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(0);
  return v.toFixed(abs >= 10 ? 1 : 2);
}

const HEIGHT = 46;
const PAD_X = 10;

/**
 * The window's distribution of one parameter, drawn the same way as the APS
 * size-distribution box plot: whiskers p05–p95, box p25–p75, a median line and
 * a mean dot — laid horizontally — plus a diamond for the latest reading so
 * you can see where "now" sits in the spread.
 */
export default function ParameterBoxPlot({
  stats,
  color
}: {
  stats: ParameterStats | null;
  color: string;
}): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref);

  if (!stats) {
    return (
      <div ref={ref} className="flex h-[46px] items-center text-xs text-gray-500">
        No readings in this window
      </div>
    );
  }

  const span = stats.max - stats.min || Math.abs(stats.max) || 1;
  const lo = stats.min - span * 0.02;
  const hi = stats.max + span * 0.02;
  const plotW = Math.max(0, width - PAD_X * 2);
  const x = (v: number): number => PAD_X + ((v - lo) / (hi - lo || 1)) * plotW;
  const mid = 18;

  return (
    <div ref={ref} className="w-full">
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label="Distribution in window">
          <line x1={x(stats.p05)} x2={x(stats.p95)} y1={mid} y2={mid} stroke={color} strokeWidth={1} />
          <line x1={x(stats.p05)} x2={x(stats.p05)} y1={mid - 5} y2={mid + 5} stroke={color} />
          <line x1={x(stats.p95)} x2={x(stats.p95)} y1={mid - 5} y2={mid + 5} stroke={color} />
          <rect
            x={x(stats.p25)}
            y={mid - 9}
            width={Math.max(1, x(stats.p75) - x(stats.p25))}
            height={18}
            fill={color}
            fillOpacity={0.22}
            stroke={color}
            rx={2}
          />
          <line x1={x(stats.p50)} x2={x(stats.p50)} y1={mid - 9} y2={mid + 9} stroke={color} strokeWidth={2} />
          <circle cx={x(stats.mean)} cy={mid} r={2.5} fill="#F9FAFB" />
          <path
            d={`M ${x(stats.latest)} ${mid - 7} l 5 7 l -5 7 l -5 -7 z`}
            fill="#FDB022"
            stroke="#101828"
            strokeWidth={0.75}
          >
            <title>Latest {fmt(stats.latest)}</title>
          </path>
          <text x={PAD_X} y={HEIGHT - 3} fontSize={11} fill="#98A2B3">
            {fmt(stats.min)}
          </text>
          <text x={width - PAD_X} y={HEIGHT - 3} fontSize={11} fill="#98A2B3" textAnchor="end">
            {fmt(stats.max)}
          </text>
          <text x={width / 2} y={HEIGHT - 3} fontSize={11} fill="#98A2B3" textAnchor="middle">
            median {fmt(stats.p50)} · box p25–p75 · whiskers p5–p95
          </text>
        </svg>
      )}
    </div>
  );
}
